import { getRankedVariableRelevance } from './variablePreset.util'

export const healthVariableBaseRelevance = {
  attendance: 0.9,
  attendanceUrgencyCare: 0.88,
  attendanceSpontaneousDemand: 0.76,
  attendanceContinuedCare: 0.7,
  attendanceOccurrence: 0.98,
  immunobiological: 0.6,
  homeVisit: 0.78,
  violence: 0.89,
  sexualViolence: 0.94,
  citizenHealth: 0.72,
  citizenHealthCondition: 0.54,
  citizenHealthConditionPercentage: 0.5,
  citizenHealthProfile: 0.72,
  citizenHealthProfileCondition: 0.54,
  citizenHealthProfileConditionPercentage: 0.5,
  citizen: 0.7,
  residence: 0.64,
  residenceCondition: 0.48,
  residenceConditionPercentage: 0.44,
  prescribedDrugs: 0.64,
  attendanceMedicalProcedure: 0.58,
  medicalProcedure: 0.54,
  bmi: 0.5,
  healthUnit: 0.58,
  healthTeam: 0.54,
  healthProfessional: 0.5,
  questionnaireCitizenHealthProfile: 0.4,
} as const

function clampRelevance(value: number, minimum: number, maximum = 1) {
  return Math.max(minimum, Math.min(maximum, value))
}

function safeScore(value: number | null | undefined) {
  if (!Number.isFinite(value)) {
    return 0
  }

  return Math.max(0, Number(value))
}

function getBestTextBoost(
  text: string,
  boosts: Array<{
    pattern: RegExp
    boost: number
  }>,
) {
  return boosts.reduce(
    (best, item) => (item.pattern.test(text) ? Math.max(best, item.boost) : best),
    0,
  )
}

const immunobiologicalPriorityBoosts = [
  { pattern: /dengue/i, boost: 0.14 },
  { pattern: /covid|sars|corona/i, boost: 0.12 },
  { pattern: /influenza|gripe/i, boost: 0.1 },
  { pattern: /sarampo|rub[eé]ola|caxumba|tr[ií]plice viral/i, boost: 0.09 },
  { pattern: /p[oó]lio|poliomiel/i, boost: 0.09 },
  { pattern: /meningoc|pneumoc/i, boost: 0.08 },
  { pattern: /hepatite/i, boost: 0.08 },
  { pattern: /febre amarela/i, boost: 0.08 },
  { pattern: /bcg|tubercul/i, boost: 0.06 },
  { pattern: /rota|rotav[ií]rus/i, boost: 0.06 },
]

export function getImmunobiologicalPresetRelevance(index: number, label: string) {
  const ranked = getRankedVariableRelevance(index, {
    initial: 0.76,
    step: 0.003,
    minimum: 0.58,
  })

  const boost = getBestTextBoost(label, immunobiologicalPriorityBoosts)
  return clampRelevance(ranked + boost, 0.58, 0.92)
}

const attendanceOccurrenceChapterRelevanceByCode: Record<string, number> = {
  A: 0.97,
  R: 0.95,
  B: 0.87,
  D: 0.83,
  X: 0.82,
  Y: 0.8,
  U: 0.78,
  S: 0.76,
  W: 0.74,
  K: 0.7,
  T: 0.68,
  P: 0.66,
  N: 0.66,
  L: 0.62,
  H: 0.6,
  F: 0.6,
  Z: 0.52,
  '-': 0.5,
}

export function getAttendanceOccurrenceChapterRelevance(code: string) {
  return attendanceOccurrenceChapterRelevanceByCode[code] ?? 0.54
}

export function getAttendanceOccurrenceDiseaseRelevance(params: {
  isTransmissible?: number | null
  isSymptom?: number | null
  isZoonosis?: number | null
  isSti?: number | null
}) {
  const transmissible = safeScore(params.isTransmissible)
  const symptom = safeScore(params.isSymptom)
  const zoonosis = safeScore(params.isZoonosis)
  const sti = safeScore(params.isSti)
  const humanToHuman = Math.max(0, transmissible - zoonosis)

  const relevance =
    0.56 +
    0.24 * transmissible +
    0.12 * humanToHuman +
    0.08 * sti +
    0.04 * zoonosis +
    0.02 * symptom

  return clampRelevance(relevance, 0.48, 0.99)
}

const prescribedDrugCategoryBoosts = [
  { pattern: /psicotr[oó]p|entorpec|controlad|tarja preta/i, boost: 0.08 },
  { pattern: /antimicrob|antibi[oó]t|anti[- ]?infecc/i, boost: 0.07 },
  { pattern: /alto risco|uso restrito/i, boost: 0.05 },
]

const prescribedDrugNameBoosts = [
  {
    pattern:
      /amoxic|azitrom|cefal|ceftri|cipro|doxicicl|nitrofur|penicil|rifamp|isoniaz|oseltamivir/i,
    boost: 0.05,
  },
]

export function getPrescribedDrugPresetRelevance(params: {
  index: number
  activeIngredient: string
  description?: string | null
  illicitUsePotential?: number | null
  illicitUseCategory?: string | null
}) {
  const ranked = getRankedVariableRelevance(params.index, {
    initial: 0.52,
    step: 0.002,
    minimum: 0.34,
  })

  const categoryText = [params.illicitUseCategory, params.activeIngredient, params.description]
    .filter(Boolean)
    .join(' ')
  const explicitRiskBoost = getBestTextBoost(categoryText, prescribedDrugCategoryBoosts)
  const infectiousUseBoost = getBestTextBoost(
    [params.activeIngredient, params.description].filter(Boolean).join(' '),
    prescribedDrugNameBoosts,
  )

  return clampRelevance(
    ranked + 0.12 * safeScore(params.illicitUsePotential) + explicitRiskBoost + infectiousUseBoost,
    0.34,
    0.8,
  )
}

export function getAttendanceMedicalProcedurePresetRelevance(index: number) {
  return getRankedVariableRelevance(index, {
    initial: 0.44,
    step: 0.0015,
    minimum: 0.28,
  })
}

export function getMedicalProcedurePresetRelevance(index: number) {
  return getRankedVariableRelevance(index, {
    initial: 0.42,
    step: 0.0015,
    minimum: 0.26,
  })
}

export function getHealthUnitPresetRelevance(index: number, isActive: boolean) {
  const ranked = getRankedVariableRelevance(index, {
    initial: 0.56,
    step: 0.004,
    minimum: 0.4,
  })

  return clampRelevance(ranked + (isActive ? 0.03 : -0.04), 0.34, 0.62)
}

export function getHealthTeamPresetRelevance(index: number, isActive: boolean) {
  const ranked = getRankedVariableRelevance(index, {
    initial: 0.52,
    step: 0.004,
    minimum: 0.38,
  })

  return clampRelevance(ranked + (isActive ? 0.03 : -0.04), 0.32, 0.58)
}

export function getHealthProfessionalPresetRelevance(index: number) {
  return getRankedVariableRelevance(index, {
    initial: 0.48,
    step: 0.003,
    minimum: 0.36,
  })
}

export function getBmiClassificationRelevance(classification: string) {
  const normalized = classification.trim().toLowerCase()

  if (normalized === 'obesidade') {
    return 0.56
  }

  if (normalized === 'abaixo do peso') {
    return 0.52
  }

  if (normalized === 'sobrepeso') {
    return 0.46
  }

  if (normalized === 'peso normal') {
    return 0.36
  }

  return healthVariableBaseRelevance.bmi
}
